import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Pencil, Upload, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useOrganizationStore } from "@/store/organizationStore";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { UnsavedChangesBar } from "@/modules/standardAgent/components/UnsavedChangesBar";
import { toast } from "sonner";
import { fetchApi } from "@/lib/api";
import { Address } from "@/lib/types";
import { captureException } from "@/lib/analytics";
import type { CurrencyCode } from "@forgepoint/shared";
import {
  CURRENCY_CODES,
  ISO_3166_1_ALPHA_2_CODES,
  isISO31661Alpha2Code,
} from "@forgepoint/shared";
import { ExchangeRatesSettings } from "./ExchangeRatesSettings";

/**
 * Creates a default Address object with all fields set to null.
 * @returns Default address object
 */
function createDefaultAddress(): Address {
  return {
    name: null,
    address_line_1: null,
    address_line_2: null,
    city: null,
    zipcode: null,
    state: null,
    country: null,
  };
}

interface OrganizationSettingsPanelProps {
  /** Whether the current user has admin privileges */
  isAdmin: boolean;
}

/**
 * Organization settings panel using the standard settings UX pattern.
 * Grouped into Organization Details, Contact Information, Address, and Currency sections
 * with SettingsRow components, sheets for complex fields, and skeleton loading.
 * @param props - OrganizationSettingsPanelProps
 * @returns Organization settings panel element
 */
export function OrganizationSettingsPanel({
  isAdmin,
}: OrganizationSettingsPanelProps) {
  const organization = useOrganizationStore((state) => state.organization);
  const orgLoading = useOrganizationStore((state) => state.loading);
  const orgError = useOrganizationStore((state) => state.error);
  const updateOrganization = useOrganizationStore(
    (state) => state.updateOrganization,
  );

  // ── Standard agent state ──
  const [isUpdatingStandardAgent, setIsUpdatingStandardAgent] = useState(false);
  const [showDisableAgentDialog, setShowDisableAgentDialog] = useState(false);

  // ── Multi-currency state ──
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(false);

  // ── Base currency state ──
  const [baseCurrency, setBaseCurrency] = useState<CurrencyCode>("USD");

  // ── Contact form state ──
  const [phone, setPhone] = useState("");
  const [fax, setFax] = useState("");
  const [website, setWebsite] = useState("");

  // ── Address form state ──
  const [address, setAddress] = useState<Address>(createDefaultAddress());
  const [addressSheetOpen, setAddressSheetOpen] = useState(false);

  // ── Logo state ──
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Org save state ──
  const [isSaving, setIsSaving] = useState(false);
  const [showCurrencyChangeWarning, setShowCurrencyChangeWarning] =
    useState(false);

  // ── Derived state ──
  const isDirty = useMemo(() => {
    if (!organization) {
      return false;
    }

    if (phone !== (organization.phone || "")) {
      return true;
    }
    if (fax !== (organization.fax || "")) {
      return true;
    }
    if (website !== (organization.website || "")) {
      return true;
    }
    if (baseCurrency !== (organization.baseCurrency ?? "USD")) {
      return true;
    }
    if (multiCurrencyEnabled !== (organization.multiCurrencyEnabled ?? false)) {
      return true;
    }
    if (
      JSON.stringify(address) !==
      JSON.stringify(organization.address || createDefaultAddress())
    ) {
      return true;
    }

    return false;
  }, [
    organization,
    phone,
    fax,
    website,
    baseCurrency,
    multiCurrencyEnabled,
    address,
  ]);

  /**
   * Resets all form fields to match the current stored settings.
   * Used on initial load and when discarding unsaved changes.
   */
  const syncFormFromSettings = useCallback(() => {
    if (organization) {
      setPhone(organization.phone || "");
      setFax(organization.fax || "");
      setWebsite(organization.website || "");
      setBaseCurrency(organization.baseCurrency ?? "USD");
      setMultiCurrencyEnabled(organization.multiCurrencyEnabled ?? false);
      setAddress(organization.address || createDefaultAddress());
    }
  }, [organization]);

  useEffect(() => {
    syncFormFromSettings();
  }, [syncFormFromSettings]);

  // ── Handlers ──
  /**
   * Toggles the standard agent on or off. Shows a confirmation dialog when disabling.
   * @param checked - Whether the agent should be enabled
   */
  const handleStandardAgentToggle = useCallback(
    async (checked: boolean) => {
      if (!organization) {
        return;
      }

      if (!checked) {
        setShowDisableAgentDialog(true);
        return;
      }

      setIsUpdatingStandardAgent(true);
      const success = await updateOrganization({
        standardAgentEnabled: checked,
      });
      setIsUpdatingStandardAgent(false);

      if (!success) {
        toast.error("Failed to update standard agent setting.");
        return;
      }

      toast.success("Standard agent enabled.");
    },
    [organization, updateOrganization],
  );

  /**
   * Confirms disabling the standard agent after the user accepts the dialog.
   */
  const confirmDisableStandardAgent = useCallback(async () => {
    setShowDisableAgentDialog(false);
    setIsUpdatingStandardAgent(true);
    const success = await updateOrganization({
      standardAgentEnabled: false,
    });
    setIsUpdatingStandardAgent(false);

    if (!success) {
      toast.error("Failed to update standard agent setting.");
      return;
    }

    toast.success("Standard agent disabled.");
  }, [updateOrganization]);

  /**
   * Handles logo file upload via presigned URL and associates the asset with the org.
   * @param file - The image file selected by the user
   */
  const handleLogoUpload = useCallback(
    async (file: File) => {
      setIsUploadingLogo(true);

      try {
        const response = await fetchApi("api/assets/upload", "POST", {
          body: {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
          },
        });

        if (!response.ok || !response.data) {
          throw new Error("Failed to get upload URL");
        }

        const { uploadUrl, asset } = response.data as {
          uploadUrl: string;
          asset: { id: string };
        };

        await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        await updateOrganization({
          logoAssetId: asset.id,
        });

        toast.success("Logo uploaded successfully");
      } catch (error) {
        captureException(
          error instanceof Error ? error : new Error(String(error)),
        );
        toast.error("Failed to upload logo", {
          description:
            "There was an error processing your request. Please try again.",
        });
      } finally {
        setIsUploadingLogo(false);
      }
    },
    [updateOrganization],
  );

  /**
   * Removes the organization logo.
   */
  const handleRemoveLogo = useCallback(async () => {
    setIsUploadingLogo(true);
    await updateOrganization({ logoAssetId: undefined });
    setIsUploadingLogo(false);
  }, [updateOrganization]);

  /**
   * Persists organization contact info, address, and currency settings to the backend.
   */
  const executeSave = useCallback(async () => {
    if (!organization) {
      return;
    }

    setIsSaving(true);
    const success = await updateOrganization({
      phone,
      fax,
      website,
      address,
      baseCurrency,
      multiCurrencyEnabled,
    });
    setIsSaving(false);

    if (!success) {
      toast.error("Failed to save organization settings.");
      return;
    }
    toast.success("Settings saved.");
  }, [
    organization,
    updateOrganization,
    phone,
    fax,
    website,
    address,
    baseCurrency,
    multiCurrencyEnabled,
  ]);

  /**
   * Saves organization settings, showing a warning dialog first when the base
   * currency is being changed.
   */
  const handleSave = useCallback(async () => {
    if (!organization) {
      return;
    }

    if (baseCurrency !== (organization.baseCurrency ?? "USD")) {
      setShowCurrencyChangeWarning(true);
      return;
    }

    await executeSave();
  }, [organization, baseCurrency, executeSave]);

  // ── Early returns ──
  if (orgLoading) {
    return <OrganizationSettingsSkeleton />;
  }

  if (orgError) {
    return (
      <Card className="border-destructive/40 shadow-sm">
        <CardContent className="py-6 text-sm text-destructive">
          {orgError}
        </CardContent>
      </Card>
    );
  }

  const addressConfigured = hasAddressContent(address);
  const logoSrc =
    organization?.logoUrl ||
    (organization?.logoAssetId
      ? `/api/assets/${organization.logoAssetId}`
      : null);

  return (
    <>
      <div className="space-y-6 pt-4">
        {/* ── Organization Details ── */}
        <SectionLabel title="Organization Details" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="organization-name"
              label="Organization Name"
              description="Your organization's display name."
            >
              <span className="text-sm text-muted-foreground">
                {organization?.name || "--"}
              </span>
            </SettingsRow>

            <SettingsRow
              id="organization-logo"
              label="Organization Logo"
              description="Appears on quotes and documents."
            >
              <div className="flex items-center gap-3">
                {logoSrc ? (
                  <div className="relative group">
                    <div className="h-10 w-10 rounded border bg-muted flex items-center justify-center overflow-hidden transition-shadow group-hover:shadow-md">
                      <img
                        src={logoSrc}
                        alt="Organization Logo"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        disabled={isUploadingLogo}
                        className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground/50">
                    No logo
                  </span>
                )}
                {isAdmin && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploadingLogo}
                    >
                      <Upload className="mr-1.5 h-3 w-3" />
                      Upload
                      {isUploadingLogo && (
                        <Loader2 className="ml-1.5 h-3 w-3 animate-spin" />
                      )}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleLogoUpload(file);
                          e.target.value = "";
                        }
                      }}
                    />
                  </>
                )}
              </div>
            </SettingsRow>

            <SettingsRow
              id="organization-standard-agent"
              label="Standard Agent"
              description="Enable or disable the standard agent for your organization."
            >
              <div className="flex items-center gap-2">
                {isUpdatingStandardAgent && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                )}
                <Switch
                  checked={organization?.standardAgentEnabled === true}
                  onCheckedChange={handleStandardAgentToggle}
                  disabled={isUpdatingStandardAgent || !isAdmin}
                />
              </div>
            </SettingsRow>
          </div>
        </Card>

        {/* ── Contact Information ── */}
        <SectionLabel title="Contact Information" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="organization-phone"
              label="Phone Number"
              description="Primary contact number for your organization."
            >
              <Input
                className="w-[220px]"
                placeholder="(123) 456-7890"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={!isAdmin}
              />
            </SettingsRow>

            <SettingsRow
              id="organization-fax"
              label="Fax Number"
              description="Fax number for document delivery."
            >
              <Input
                className="w-[220px]"
                placeholder="(123) 456-7890"
                value={fax}
                onChange={(e) => setFax(e.target.value)}
                disabled={!isAdmin}
              />
            </SettingsRow>

            <SettingsRow
              id="organization-website"
              label="Website"
              description="Your organization's public website."
            >
              <Input
                className="w-[260px]"
                placeholder="https://example.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                disabled={!isAdmin}
              />
            </SettingsRow>
          </div>
        </Card>

        {/* ── Address ── */}
        <SectionLabel title="Address" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="organization-address"
              label="Organization Address"
              description="Mailing address used on quotes and documents."
            >
              <div className="flex items-center gap-2">
                <ConfiguredIndicator configured={addressConfigured} />
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAddressSheetOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" />
                    Edit
                  </Button>
                )}
              </div>
            </SettingsRow>
          </div>
        </Card>

        {/* ── Currency ── */}
        <SectionLabel title="Currency" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="organization-multi-currency"
              label="Multi-Currency"
              description="Allow quotes to be priced in currencies other than your base currency."
            >
              <Switch
                checked={multiCurrencyEnabled}
                onCheckedChange={setMultiCurrencyEnabled}
                disabled={!isAdmin}
              />
            </SettingsRow>

            {multiCurrencyEnabled && (
              <SettingsRow
                id="organization-base-currency"
                label="Base Currency"
                description="The currency used as the basis for quote pricing."
              >
                <div className="flex items-center gap-2">
                  <Select
                    value={baseCurrency}
                    onValueChange={(value) => {
                      const code = CURRENCY_CODES.find((c) => c === value);
                      if (code) {
                        setBaseCurrency(code);
                      }
                    }}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_CODES.map((code) => (
                        <SelectItem key={code} value={code}>
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </SettingsRow>
            )}
          </div>
        </Card>

        {multiCurrencyEnabled && <ExchangeRatesSettings />}

        <UnsavedChangesBar
          isDirty={isAdmin && isDirty}
          onSave={handleSave}
          onDiscard={syncFormFromSettings}
          isSaving={isSaving}
        />
      </div>

      {/* ── Disable Agent Dialog ── */}
      <AlertDialog
        open={showDisableAgentDialog}
        onOpenChange={setShowDisableAgentDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Standard Agent?</AlertDialogTitle>
            <AlertDialogDescription>
              Disabling the standard agent will stop all automated agent
              workflows for your organization. You can re-enable it at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisableStandardAgent}>
              Disable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Base Currency Change Warning Dialog ── */}
      <AlertDialog
        open={showCurrencyChangeWarning}
        onOpenChange={setShowCurrencyChangeWarning}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Base Currency Change Alert</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground space-y-2">
                <p>
                  Warning! Changing your base currency affects all existing
                  items and will cause all historical and future quotes to be
                  re-priced in the new base currency.
                </p>
                <p>Please contact support before making this change.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeSave}>
              Change anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Address Sheet ── */}
      <AddressSheet
        open={addressSheetOpen}
        onOpenChange={setAddressSheetOpen}
        value={address}
        onApply={setAddress}
      />
    </>
  );
}

// ── Local Components ──

/**
 * Sheet for editing the organization address with local state and Apply/Cancel buttons.
 * @param props - Sheet props including open state, address value, and apply callback
 * @returns Address editing sheet element
 */
function AddressSheet({
  open,
  onOpenChange,
  value,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: Address;
  onApply: (address: Address) => void;
}) {
  const [local, setLocal] = useState<Address>(createDefaultAddress());

  useEffect(() => {
    if (open) {
      setLocal({ ...value });
    }
  }, [open, value]);

  /**
   * Updates a single field in the local address state.
   * @param field - The address field key to update
   * @param fieldValue - The new value for the field
   */
  const updateField = (field: keyof Address, fieldValue: string) => {
    setLocal((prev) => ({
      ...prev,
      [field]: fieldValue || null,
    }));
  };

  /**
   * Updates the country when a valid ISO alpha-2 option is selected.
   * @param country - Selected country code
   */
  const handleCountrySelect = (country: string) => {
    if (isISO31661Alpha2Code(country)) {
      updateField("country", country);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[480px] sm:max-w-[480px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Organization Address</SheetTitle>
          <SheetDescription>
            Mailing address used on quotes and documents.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Address Line 1
            </Label>
            <Input
              placeholder="123 Main St"
              value={local.address_line_1 || ""}
              onChange={(e) => updateField("address_line_1", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Address Line 2
            </Label>
            <Input
              placeholder="Suite 100"
              value={local.address_line_2 || ""}
              onChange={(e) => updateField("address_line_2", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">City</Label>
              <Input
                placeholder="City"
                value={local.city || ""}
                onChange={(e) => updateField("city", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                State / Province
              </Label>
              <Input
                placeholder="State"
                value={local.state || ""}
                onChange={(e) => updateField("state", e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                ZIP / Postal Code
              </Label>
              <Input
                placeholder="ZIP Code"
                value={local.zipcode || ""}
                onChange={(e) => updateField("zipcode", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Country</Label>
              <Select
                value={local.country || ""}
                onValueChange={handleCountrySelect}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {ISO_3166_1_ALPHA_2_CODES.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <SheetFooter className="pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onApply(local);
              onOpenChange(false);
            }}
          >
            Apply
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Skeleton loading state that mirrors the organization settings layout.
 * Shows placeholder rows for Organization Details, Contact Information, and Address sections.
 * @returns Skeleton layout element
 */
function OrganizationSettingsSkeleton() {
  return (
    <div className="space-y-6 pt-4">
      {/* Organization Details */}
      <Skeleton className="h-5 w-36" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonRow key={`details-${i}`} />
          ))}
        </div>
      </Card>

      {/* Contact Information */}
      <Skeleton className="h-5 w-36" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonRow key={`contact-${i}`} />
          ))}
        </div>
      </Card>

      {/* Address */}
      <Skeleton className="h-5 w-16" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SkeletonRow />
        </div>
      </Card>
    </div>
  );
}

/**
 * Single skeleton row matching the SettingsRow layout.
 * @returns Skeleton row element
 */
function SkeletonRow() {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-56" />
      </div>
      <Skeleton className="h-8 w-[200px] rounded-md" />
    </div>
  );
}

/**
 * Standalone section label rendered above a Card as a lightweight heading.
 * @param props - Contains a title string
 * @returns Section label element
 */
function SectionLabel({ title }: { title: string }) {
  return <h3 className="text-lg font-semibold tracking-tight pl-2">{title}</h3>;
}

/**
 * Small status indicator showing whether a text-based setting has been configured.
 * Displays a green dot with "Configured" or muted "Not set" text.
 * @param props - Contains a configured boolean
 * @returns Status indicator element
 */
function ConfiguredIndicator({ configured }: { configured: boolean }) {
  if (configured) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Configured
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground/50">Not set</span>;
}

// ── Utility Functions ──

/**
 * Checks whether any address field has non-empty content.
 * @param addr - The address object to check
 * @returns True if at least one address field has content
 */
function hasAddressContent(addr: Address): boolean {
  return [
    addr.address_line_1,
    addr.address_line_2,
    addr.city,
    addr.state,
    addr.zipcode,
    addr.country,
  ].some((field) => field !== null && field.trim().length > 0);
}
